import React, { SyntheticEvent, useRef } from 'react';
import { Person } from 'talent_matching/types';
import { Stack } from '@grnhse/seedling/lib/birch/layouts/stack_v2';
import { withLocalePrefix } from 'shared/utils/translation';
import { FullPalette } from '@grnhse/seedling/lib/birch/colors';
import styled from 'styled-components';
import StyledTag, {
  NeutralTag,
  SecondaryTag,
  SuccessTag,
  WarningTag,
  DangerTag,
} from 'talent_matching/components/shared/styled_tag';
import { TertiaryButton } from '@grnhse/seedling/lib/birch/components/buttons';
import { useModal } from '@grnhse/seedling/lib/birch/components/modals';
import Popover from '@mui/material/Popover';

const ClickableDangerTag = styled.button`
  cursor: pointer;
  background: none;
  border: none;
  padding: 0;
  margin: 0;
`;

const t = withLocalePrefix('talent_matching.candidate_chips');

type Props = {
  application: Person;
  showTags?: boolean;
  onDuplicateClick?: () => void;
};

// Fixes issue where some tags are taller than others
const ChipRow = styled(Stack)`
  div {
    max-height: 20px;
  }
`;

const CandidateChips = ({ application, showTags, onDuplicateClick }: Props) => {
  const [popperIsOpen, openPopper, closePopper] = useModal();
  const anchorEl = useRef<HTMLDivElement | null>(null);

  const tagsToShow = showTags ? application.tags.slice(0, 6) : [];
  const hasMoreTags = showTags && application.tags.length > 6;

  const onTagsClick = (e: SyntheticEvent) => {
    e.stopPropagation();
    openPopper();
  };

  return (
    <ChipRow
      direction="row"
      flexWrap="wrap"
      spacing={1}
      sx={{
        flexShrink: 1,
      }}
    >
      {application.referral && <SuccessTag text={t('referral')} />}
      {application.internal && <SuccessTag text={t('internal')} />}
      {application.fromAgency && (
        <SecondaryTag key={t('agency')} text={t('agency')} />
      )}
      {application.dreamJob && (
        <StyledTag text={t('dream_job')} bgColor={FullPalette.MARIGOLD_E100} />
      )}
      {application.previouslyRejectedFromJob && (
        <WarningTag text={t('previously_rejected')} />
      )}
      {application.activeOnAnotherJob && (
        <WarningTag text={t('active_on_another_job')} />
      )}
      {application.potentialDuplicate && onDuplicateClick && (
        <ClickableDangerTag
          aria-label={t('potential_duplicate')}
          onClick={(e: SyntheticEvent) => {
            e.stopPropagation();
            onDuplicateClick();
          }}
        >
          <DangerTag text={t('potential_duplicate')} />
        </ClickableDangerTag>
      )}
      {application.potentialDuplicate && !onDuplicateClick && (
        <DangerTag text={t('potential_duplicate')} />
      )}
      {tagsToShow.map((tag) => (
        <NeutralTag key={tag.value} text={tag.label} />
      ))}
      {hasMoreTags && (
        <div ref={anchorEl}>
          <TertiaryButton size="md" onClick={onTagsClick}>
            {t('view_all_tags')}
          </TertiaryButton>
          <Popover
            open={popperIsOpen}
            onClose={closePopper}
            anchorEl={anchorEl.current}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'left',
            }}
          >
            <Stack
              direction="row"
              flexWrap="wrap"
              spacing={1}
              sx={{ padding: 2, maxWidth: '300px' }}
            >
              {application.tags.map((tag) => (
                <NeutralTag key={tag.value} text={tag.label} />
              ))}
            </Stack>
          </Popover>
        </div>
      )}
    </ChipRow>
  );
};

export default CandidateChips;
